import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Constants from '../../submodules/logictry_config/constants';
import Navigation from '../../services/Navigation';
import WindowSize from '../../services/WindowSize';
import UserAccount from '../../services/UserAccount';
import { getNotifications } from '../../services/Pagination';
import UserAvatar from '../UserAvatar';
import Status from '../Header/Status';
import AccountMenu, { getLogicBaseAccountSections } from '../AccountMenu';
import LogicBaseSearchBar from '../LogicBaseSearchBar';
import LogicBaseCollectionEditPopup from '../LogicBaseCollectionEditPopup';
import LogicBaseNotificationPopupService from '../../services/LogicBaseNotificationPopup';
import { BellIcon, BoxPlusIcon } from '../../styledhtml/Icon';
import IconBadgeDot from '../IconBadgeDot';
import LogictryComponent from '../../submodules/logictry_config/LogictryComponent';

const Wrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  background-color: transparent;
  padding: 0.1rem ${Constants.LogicBasePageGutter} 0.35rem;
  box-shadow: none;
  box-sizing: border-box;
  ${Constants.MediaMobile} {
    height: auto;
    min-height: 2.75rem;
    padding: 0.15rem ${Constants.LogicBasePageGutterCompact} 0.3rem;
  }
  a {
    text-decoration: none;
  }
`;

const HomeLogoBtn = styled.button`
  display: flex;
  align-items: center;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0.35rem 0.5rem 0.35rem 0;
  margin: 0;
  flex-shrink: 0;
  border-radius: 10px;

  img {
    height: 32px;
    width: 108px;
    object-fit: contain;
    display: block;
    @media (max-width: 370px) {
      height: 24px;
      width: 80px;
    }
  }
`;

const Links = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.1rem;
  flex-shrink: 0;
`;

const IconBtn = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 12px;
  background: ${(p) => (p.$active
    ? (Constants.isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.92)')
    : 'transparent')};
  box-shadow: ${(p) => (p.$active
    ? (Constants.isDarkMode ? 'none' : '0 2px 10px rgba(0, 0, 0, 0.08)')
    : 'none')};
  cursor: pointer;
  padding: 0;
  color: ${Constants.DarkTextColor};
  transition: background 0.12s, box-shadow 0.12s;

  &:hover {
    background: ${Constants.isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.72)'};
  }
  > * {
    ${Constants.isDarkMode ? 'filter: invert(1);' : ''}
  }
`;

const ProfileBtn = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.15rem 0 0.15rem 0.25rem;
  cursor: pointer;
`;

const SignInButton = styled.div`
  background: ${Constants.isDarkMode ? 'transparent' : Constants.MediumBackground};
  border: ${Constants.isDarkMode ? `1px solid ${Constants.HairlineBorderRgba}` : 'none'};
  color: ${Constants.DarkTextColor};
  border-radius: 2rem;
  font-weight: 500;
  font-size: ${Constants.SemiSmallFontSize};
  padding: 0.5rem 1rem;
  ${Constants.MediaMobile} {
    font-size: ${Constants.SmallFontSize};
  }
  @media (max-width: 370px) {
    padding: 0.5rem;
  }
  margin: 0 0.25rem 0 0.5rem;
  cursor: pointer;
`;

const GetAppButton = styled.div`
  background: ${Constants.DarkTextColor};
  color: ${Constants.isDarkMode ? '#000' : '#fff'};
  border-radius: 0.5rem;
  font-weight: 500;
  font-size: ${Constants.SemiSmallFontSize};
  padding: 0.5rem 1rem;
  ${Constants.MediaMobile} {
    font-size: ${Constants.SmallFontSize};
  }
  @media (max-width: 370px) {
    padding: 0.5rem;
  }
  margin: 0 0.5rem;
  cursor: pointer;
`;

const MobileHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.15rem;
  flex-shrink: 0;
`;

export default class LogicBaseHeader extends LogictryComponent {
  static propTypes = {
    topBarLayout: PropTypes.bool,
    mobileDockLayout: PropTypes.bool,
    onGoHome: PropTypes.func,
    onCreateCollection: PropTypes.func,
    onOpenNotifications: PropTypes.func,
    notificationBadge: PropTypes.bool,
    homeActive: PropTypes.bool,
  };

  state = {
    open: false,
    showCreateCollection: false,
  };

  componentDidMount() {
    Navigation.onStateUpdate(this);
    WindowSize.onStateUpdate(this);
    UserAccount.onStateUpdate(this);
  }

  componentWillUnmount() {
    Navigation.offStateUpdate(this);
    WindowSize.offStateUpdate(this);
    UserAccount.offStateUpdate(this);
    if (this.notifications) this.notifications.offStateUpdate(this);
    this.notifications = null;
  }

  clickedAccount = () => this.setState({ open: true });

  close = () => this.setState({ open: false });

  openCreateCollection = () => {
    const { onCreateCollection } = this.props;
    if (onCreateCollection) {
      onCreateCollection();
      return;
    }
    if (!UserAccount.isLoggedIn) UserAccount.showLoginPopup();
    else this.setState({ showCreateCollection: true });
  };

  openNotifications = () => {
    const { onOpenNotifications } = this.props;
    if (onOpenNotifications) onOpenNotifications();
    else LogicBaseNotificationPopupService.open();
  };

  ensureNotificationBadge = () => {
    const { notificationBadge } = this.props;
    if (notificationBadge !== undefined) return notificationBadge;

    const { isLoggedIn, account } = UserAccount;
    if (!isLoggedIn) {
      if (this.notifications) this.notifications.offStateUpdate(this);
      this.notifications = null;
      return false;
    }
    if (!this.notifications) {
      this.notifications = getNotifications();
      this.notifications.onStateUpdate(this);
    }
    let badge = !!this.notifications.currentPage.find(({ read }) => !read);
    if (!account.username || !account.fullname || !account.image?.link || !account.password) {
      badge = true;
    }
    return badge;
  };

  renderLoggedOutActions = () => (
    <Links>
      <SignInButton onClick={() => { UserAccount.showLoginPopup(); }}>
        Sign In
      </SignInButton>
      {!Constants.isIphone && (
        <GetAppButton
          onClick={() => {
            if (Constants.isIOS) window.location = Constants.AppleStore;
            else if (Constants.isAndroid) window.location = Constants.GoogleStore;
            else UserAccount.showGetAppPopup();
          }}
        >
          Get App
        </GetAppButton>
      )}
    </Links>
  );

  renderTopBarActions = (badge) => {
    const { account } = UserAccount;
    const iconStyle = { width: 20, height: 20 };

    return (
      <Links>
        <IconBtn type="button" title="Create collection" aria-label="Create collection" onClick={this.openCreateCollection}>
          <BoxPlusIcon style={iconStyle} />
        </IconBtn>
        <IconBtn type="button" title="Notifications" aria-label="Notifications" onClick={this.openNotifications}>
          <BellIcon style={{ ...iconStyle, position: 'relative' }}>
            {badge && <IconBadgeDot />}
          </BellIcon>
        </IconBtn>
        <ProfileBtn>
          <UserAvatar
            user={account}
            widthAndHeight={34}
            buttonRef={(el) => { this.anchorEl = el; }}
            onClick={this.clickedAccount}
          />
        </ProfileBtn>
      </Links>
    );
  };

  render() {
    const {
      topBarLayout,
      mobileDockLayout,
      onGoHome,
      homeActive,
    } = this.props;
    const { open, showCreateCollection } = this.state;
    const { isLoggedIn, account, isDevAccount } = UserAccount;
    const useMobileDockHeader = mobileDockLayout && isLoggedIn;
    const badge = this.ensureNotificationBadge();

    return (
      <Wrapper>
        {useMobileDockHeader ? (
          <>
            <HomeLogoBtn
              type="button"
              aria-label="Home"
              onClick={() => Navigation.push(Navigation.home)}
            >
              <img src={Constants.Logo} alt="Logictry" />
            </HomeLogoBtn>
            <div style={{ flex: 1, minWidth: 0 }} />
            <MobileHeaderActions>
              <IconBtn
                type="button"
                title="Create collection"
                aria-label="Create collection"
                onClick={this.openCreateCollection}
              >
                <BoxPlusIcon style={{ width: 20, height: 20 }} />
              </IconBtn>
              <IconBtn
                type="button"
                aria-label="Notifications"
                onClick={this.openNotifications}
              >
                <BellIcon style={{ width: 20, height: 20, position: 'relative' }}>
                  {badge && <IconBadgeDot />}
                </BellIcon>
              </IconBtn>
              <ProfileBtn>
                <UserAvatar
                  user={account}
                  widthAndHeight={34}
                  buttonRef={(el) => { this.anchorEl = el; }}
                  onClick={this.clickedAccount}
                />
              </ProfileBtn>
            </MobileHeaderActions>
          </>
        ) : topBarLayout ? (
          <>
            <HomeLogoBtn
              type="button"
              title="Home"
              aria-label="Home"
              aria-current={homeActive ? 'page' : undefined}
              onClick={onGoHome || (() => Navigation.push(Navigation.home))}
            >
              <img src={Constants.Logo} alt="Logictry" />
            </HomeLogoBtn>
            <div style={{ flex: 1, minWidth: 0, padding: '0 0.75rem' }}>
              <LogicBaseSearchBar />
            </div>
            <Status hideStatusUpdates />
            {isLoggedIn ? this.renderTopBarActions(badge) : this.renderLoggedOutActions()}
          </>
        ) : (
          <>
            <HomeLogoBtn
              type="button"
              aria-label="Home"
              onClick={() => Navigation.push(Navigation.home)}
            >
              <img src={Constants.Logo} alt="Logictry" />
            </HomeLogoBtn>
            <div style={{ flex: 1, minWidth: 0 }} />
            <div style={{ flex: '0 0 auto', padding: '0 0.15rem' }}>
              <LogicBaseSearchBar />
            </div>
            <Status hideStatusUpdates />
            {!isLoggedIn && this.renderLoggedOutActions()}
          </>
        )}
        {showCreateCollection && (
          <LogicBaseCollectionEditPopup onClose={() => this.setState({ showCreateCollection: false })} />
        )}
        {isLoggedIn && (
          <AccountMenu
            open={open}
            anchorEl={this.anchorEl}
            account={account}
            onClose={this.close}
            anchorBottomRight
            sections={getLogicBaseAccountSections({
              isDevAccount,
              openNotifications: this.openNotifications,
            })}
          />
        )}
      </Wrapper>
    );
  }
}
